import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link } from 'react-router-dom';
import {
  User,
  ArrowRight,
  Mail,
  ExternalLink,
  Rocket,
  Sparkles,
  GraduationCap,
  Send,
  PenLine,
  Clock,
} from 'lucide-react';
import './App.css';
import './components/articles/Articles.css';
import Valentine from './components/Valentine';
import PrivatePortal from './components/mealprep/PrivatePortal';
import MealPrepDashboard from './components/mealprep/MealPrepDashboard';
import Particles from './components/backgrounds/Particles';
import { ShimmerButton } from './components/magicui/shimmer-button';
import { GradientTextSpan } from './components/magicui/animated-gradient-text';
import { getPublishedArticles } from './services/articlesService';
import { formatDate } from './utils/articleUtils';

// Lazy-loaded so the markdown/syntax-highlighting bundle only loads on
// article pages, keeping the main portfolio bundle small.
const ArticlesList = lazy(() => import('./components/articles/ArticlesList'));
const ArticleReader = lazy(() => import('./components/articles/ArticleReader'));
const ArticleAdmin = lazy(() => import('./components/articles/ArticleAdmin'));

// Brand icons (lucide-react dropped brand glyphs, so we inline them)
function GithubIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56 0-.28-.01-1.02-.02-2-3.2.7-3.88-1.54-3.88-1.54-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.71 1.26 3.37.96.1-.75.4-1.26.73-1.55-2.56-.29-5.25-1.28-5.25-5.69 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 2.9-.39c.98 0 1.97.13 2.9.39 2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.84 1.19 3.1 0 4.42-2.69 5.39-5.26 5.68.41.36.78 1.06.78 2.14 0 1.55-.01 2.8-.01 3.18 0 .31.21.68.8.56A11.51 11.51 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5Z" />
    </svg>
  );
}

function LinkedinIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}

// Animated Counter Component - defined outside to prevent re-renders
function AnimatedCounter({ end, duration = 2000, suffix = '' }) {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const element = countRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimatedRef.current) {
          hasAnimatedRef.current = true;
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={countRef}>{count}{suffix}</span>;
}

function AppContent() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [latestArticles, setLatestArticles] = useState([]);
  
  const fullTitle = "MuleSoft Solution Engineer @ Salesforce";
  const sectionsRef = useRef({});

  // Typing animation for hero title
  useEffect(() => {
    if (isLoading) return;
    
    let index = 0;
    const typeInterval = setInterval(() => {
      if (index <= fullTitle.length) {
        setTypedText(fullTitle.slice(0, index));
        index++;
      } else {
        clearInterval(typeInterval);
      }
    }, 50);

    return () => clearInterval(typeInterval);
  }, [isLoading]);

  // Scroll progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Loading screen
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2200);
    return () => clearTimeout(timer);
  }, []);

  // Latest published articles for the "Writing" teaser (fails gracefully)
  useEffect(() => {
    getPublishedArticles(3)
      .then(setLatestArticles)
      .catch((err) => console.warn('Could not load latest articles:', err));
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            if (entry.target.id) {
              setActiveSection(entry.target.id);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isLoading]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Data from CV
  const profile = {
    name: "Abhiraj Kane",
    title: "MuleSoft Solution Engineer @ Salesforce",
    tagline: "Connecting enterprise systems and data through robust MuleSoft integrations and Informatica-driven data management",
    email: "abhirajkane@gmail.com",
    phone: "+44 (0)7957365499",
    location: "London, UK",
    github: "https://github.com/abhirajk13",
    linkedin: "https://www.linkedin.com/in/abhirajk"
  };

  // Skills - exactly as stated in CV
  const skills = {
    ai: ["LangChain", "Prompt Engineering", "AI Agent Architecture", "Vector Database (RAG)", "Claude APIs", "Flask"],
    integration: ["MuleSoft", "REST/SOAP", "DataWeave", "Anypoint Platform"],
    development: ["Python", "Java", "C#", "C", "JavaScript", "HTML", "CSS", "Flask"],
    databases: ["SQL", "MySQL", "PostgreSQL", "SOQL"],
    devops: ["Git", "GitHub Actions", "CI/CD Pipelines", "Maven"],
    methodologies: ["Agile", "Scrum", "TDD"]
  };

  const experience = [
    {
      title: "MuleSoft Solution Engineer",
      company: "Salesforce · Data Foundations",
      period: "May 2026 – Present",
      highlights: [
        "Account Solution Engineer focused on MuleSoft, partnering with enterprise customers across EMEA on integration and automation",
        "Architect scalable, API-led connectivity solutions that connect applications, data, and AI-driven workflows across the enterprise",
        "Lead technical discovery, tailored demos, and proofs of concept that demonstrate the value of the MuleSoft platform",
        "Also advise on Informatica for data management as part of Salesforce's Data Foundations portfolio"
      ],
      tags: ["MuleSoft", "API-led", "Solution Engineering", "Informatica"]
    },
    {
      title: "MuleSoft Technical Consultant",
      company: "Salesforce · Professional Services",
      period: "May 2024 – May 2026",
      highlights: [
        "Led the design, development, and deployment of enterprise-grade integration solutions for clients across the EMEA region",
        "Built scalable APIs and batch pipelines moving large data volumes across distributed enterprise systems, including SAP",
        "Implemented DevOps and CI/CD best practices for automated, reliable deployment pipelines",
        "Delivered in-depth technical workshops and training to empower client teams to adopt modern integration patterns"
      ],
      tags: ["MuleSoft", "SAP", "API Design", "DevOps", "EMEA"]
    },
    {
      title: "MuleSoft Associate Technical Consultant",
      company: "Salesforce · Professional Services",
      period: "Sep 2022 – May 2024",
      highlights: [
        "Partnered with industry-leading EMEA clients to design and deliver integration solutions on the Anypoint Platform",
        "Led projects through the full API lifecycle — requirements, design, development, testing, and deployment",
        "Contributed to major initiatives including Mule 3 to Mule 4 migrations, Catalyst launches, and cloud adoption strategies",
        "Advised clients on modernising legacy systems with secure, governed API-led architectures"
      ],
      tags: ["MuleSoft", "Anypoint", "API-led", "Mule 3→4"]
    },
    {
      title: "MuleSoft Technical Consultant Intern",
      company: "Salesforce · MuleSoft",
      period: "Jun 2021 – Aug 2021",
      highlights: [
        "Completed intensive certification training covering API-led connectivity and the Anypoint Platform",
        "Automated CloudHub infrastructure maintenance and resource provisioning to streamline deployments",
        "Built and tested APIs following the full API development lifecycle"
      ],
      tags: ["MuleSoft", "Anypoint", "CloudHub"]
    }
  ];

  const projects = [
    {
      name: "NovaraHR",
      url: "novarahr.com",
      period: "Feb 2025 – Present",
      description: "AI-powered HR operations platform automating internal workflows",
      features: [
        "Custom RAG knowledge system using vector embeddings for policy querying",
        "Backend services in Python/Flask integrating LLM APIs",
        "Prompt engineering workflows to reduce hallucination",
        "Conversational AI interfaces for HR automation"
      ],
      tags: ["Python", "Flask", "RAG", "LLMs", "Vector DB"]
    }
  ];

  const certifications = [
    { name: "Salesforce Certified Agentforce Specialist", icon: "🧠" },
    { name: "Salesforce Certified AI Associate", icon: "🤖" },
    { name: "MuleSoft Certified Developer — Level 1", icon: "🔗" },
    { name: "Agentforce Champion", icon: "⭐" },
    { name: "AWS Cloud Computing Fundamentals", icon: "☁️" }
  ];

  const education = [
    {
      school: "University College London (UCL)",
      degree: "MSc Technology Management",
      period: "2024 – 2026",
      grade: "Distinction"
    },
    {
      school: "Royal Holloway, University of London",
      degree: "BSc Computer Science & Artificial Intelligence",
      period: "2019 – 2022",
      grade: "First Class Honours"
    }
  ];

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loader-ring">
            <span className="loader-ring-core"></span>
          </div>
          <h1 className="loading-name">
            <GradientTextSpan>Abhiraj Kane</GradientTextSpan>
          </h1>
          <p className="loading-role">MuleSoft Solution Engineer @ Salesforce</p>
          <div className="loading-bar">
            <div className="loading-progress"></div>
          </div>
        </div>
        <div className="loading-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />
      
      {/* Animated background gradient */}
      <div className="gradient-bg" />

      {/* Interactive WebGL particle field (React Bits) */}
      <div className="particles-bg" aria-hidden="true">
        <Particles
          particleColors={['#6366f1', '#8b5cf6', '#a855f7', '#ffffff']}
          particleCount={220}
          particleSpread={11}
          speed={0.09}
          particleBaseSize={95}
          moveParticlesOnHover
          particleHoverFactor={1.2}
          alphaParticles
        />
      </div>

      {/* Floating shapes */}
      <div className="floating-shapes">
        <div className="float-shape float-1"></div>
        <div className="float-shape float-2"></div>
        <div className="float-shape float-3"></div>
        <div className="float-shape float-4"></div>
      </div>
      
      {/* Navigation */}
      <nav className="navbar glass">
        <button
          className={`nav-link nav-home ${activeSection === 'hero' ? 'active' : ''}`}
          onClick={() => scrollToSection('hero')}
          aria-label="Back to top"
        >
          <User size={16} strokeWidth={2.25} />
        </button>
        {['About', 'Skills', 'Experience', 'Projects', 'Contact'].map((item) => (
          <button
            key={item}
            className={`nav-link ${activeSection === item.toLowerCase() ? 'active' : ''}`}
            onClick={() => scrollToSection(item.toLowerCase())}
          >
            {item}
          </button>
        ))}
        <button
          className="nav-link nav-articles"
          onClick={() => navigate('/articles')}
        >
          Articles
        </button>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title animate-on-scroll">
            <span className="title-line">Hi, I'm</span>
            <span className="title-name gradient-text">{profile.name}</span>
          </h1>
          <p className="hero-subtitle animate-on-scroll">
            {typedText || '\u00A0'}
          </p>
          <p className="hero-description animate-on-scroll">{profile.tagline}</p>
          
          <div className="hero-cta animate-on-scroll">
            <ShimmerButton
              className="hero-shimmer-btn"
              borderRadius="16px"
              shimmerDuration="2.5s"
              onClick={() => scrollToSection('projects')}
            >
              <span className="btn-text">View My Work</span>
              <ArrowRight size={18} strokeWidth={2.5} className="btn-arrow-icon" />
            </ShimmerButton>
            <button className="btn-secondary" onClick={() => scrollToSection('contact')}>
              <Mail size={17} strokeWidth={2.25} />
              Get In Touch
            </button>
          </div>

          <div className="hero-stats animate-on-scroll">
            <div className="stat-item">
              <span className="stat-number">
                <AnimatedCounter end={4} suffix="+" />
              </span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">
                <AnimatedCounter end={5} />
              </span>
              <span className="stat-label">Certifications</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">
                <AnimatedCounter end={10} suffix="+" />
              </span>
              <span className="stat-label">Projects Delivered</span>
            </div>
          </div>
        </div>

        <div className="hero-visual">
          <div className="code-window glass animate-on-scroll">
            <div className="window-header">
              <div className="window-dots">
                <span className="dot red"></span>
                <span className="dot yellow"></span>
                <span className="dot green"></span>
              </div>
              <span className="window-title">abhiraj.py</span>
            </div>
            <pre className="code-content">
              <code>
{`class SolutionEngineer:
    def __init__(self):
        self.name = "Abhiraj Kane"
        self.role = "MuleSoft Solution Engineer"
        self.team = "Salesforce · Data Foundations"
        self.stack = ["MuleSoft", "Informatica",
                      "Applied AI"]
    
    def build_solution(self, problem):
        return self.innovate(problem)
    
    def current_focus(self):
        return ["Data Integration",
                "Data Governance",
                "AI-Ready Data"]`}
              </code>
            </pre>
          </div>
        </div>

        <div className="scroll-indicator" onClick={() => scrollToSection('about')}>
          <div className="mouse">
            <div className="mouse-wheel"></div>
          </div>
          <span className="scroll-text">Scroll to explore</span>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about-section">
        <div className="section-header animate-on-scroll">
          <span className="section-tag">About Me</span>
          <h2 className="section-title">Building the Future with <span className="gradient-text">AI & Integration</span></h2>
        </div>

        <div className="about-content">
          <div className="about-text animate-on-scroll">
            <p className="about-lead">
              A MuleSoft Solution Engineer at Salesforce, helping enterprises across EMEA 
              connect applications, data, and AI-driven workflows through API-led integration — 
              with some Informatica for data management as part of the Data Foundations portfolio.
            </p>
            <p>
              I moved into solution engineering after several years in MuleSoft Professional Services, 
              where I designed and delivered enterprise integrations connecting platforms like 
              Salesforce and SAP for large EMEA clients—so I bring a hands-on, delivery-grounded 
              perspective to every customer conversation.
            </p>
            <p>
              Outside of my core role, I build applied AI—LLM-powered tools, multi-agent systems, 
              and RAG architectures—which keeps me close to how modern data foundations power 
              real AI in production.
            </p>
          </div>

          <div className="education-cards animate-on-scroll">
            {education.map((edu, index) => (
              <div key={index} className="edu-card glass" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="edu-icon"><GraduationCap size={26} strokeWidth={2} /></div>
                <div className="edu-content">
                  <h4>{edu.school}</h4>
                  <p className="edu-degree">{edu.degree}</p>
                  <div className="edu-meta">
                    <span>{edu.period}</span>
                    <span className="edu-grade">{edu.grade}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section skills-section">
        <div className="section-header animate-on-scroll">
          <span className="section-tag">Expertise</span>
          <h2 className="section-title">Skills & <span className="gradient-text">Technologies</span></h2>
        </div>

        <div className="skills-grid">
          {Object.entries(skills).map(([category, skillList], categoryIndex) => (
            <div 
              key={category} 
              className="skill-category animate-on-scroll glass"
              style={{ animationDelay: `${categoryIndex * 0.1}s` }}
            >
              <h3 className="category-title">
                {category === 'ai' && '🤖 AI & LLM Engineering'}
                {category === 'integration' && '🔗 MuleSoft & Integration'}
                {category === 'development' && '💻 Software Development'}
                {category === 'databases' && '🗄️ Databases'}
                {category === 'devops' && '⚙️ DevOps'}
                {category === 'methodologies' && '📋 Methodologies'}
              </h3>
              <div className="skill-tags">
                {skillList.map((skill, index) => (
                  <span 
                    key={index} 
                    className="skill-tag"
                    style={{ animationDelay: `${(categoryIndex * 0.1) + (index * 0.05)}s` }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="certifications animate-on-scroll">
          <h3 className="cert-title">Certifications</h3>
          <div className="cert-grid">
            {certifications.map((cert, index) => (
              <div 
                key={index} 
                className="cert-badge glass"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <span className="cert-icon">{cert.icon}</span>
                <span className="cert-name">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section experience-section">
        <div className="section-header animate-on-scroll">
          <span className="section-tag">Career</span>
          <h2 className="section-title">Professional <span className="gradient-text">Experience</span></h2>
        </div>

        <div className="timeline">
          {experience.map((exp, index) => (
            <div 
              key={index} 
              className="timeline-item animate-on-scroll"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                {index < experience.length - 1 && <div className="marker-line"></div>}
              </div>
              <div className="timeline-content glass">
                <div className="exp-header">
                  <div>
                    <h3 className="exp-title">{exp.title}</h3>
                    <p className="exp-company">{exp.company}</p>
                  </div>
                  <span className="exp-period">{exp.period}</span>
                </div>
                <ul className="exp-highlights">
                  {exp.highlights.map((highlight, i) => (
                    <li key={i}>{highlight}</li>
                  ))}
                </ul>
                <div className="exp-tags">
                  {exp.tags.map((tag, i) => (
                    <span key={i} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section projects-section">
        <div className="section-header animate-on-scroll">
          <span className="section-tag">Portfolio</span>
          <h2 className="section-title">Featured <span className="gradient-text">Projects</span></h2>
        </div>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <div key={index} className="project-card animate-on-scroll glass">
              <div className="project-glow"></div>
              <div className="project-header">
              <div className="project-icon"><Rocket size={22} strokeWidth={2} /></div>
              <div className="project-links">
                <a href={`https://${project.url}`} target="_blank" rel="noopener noreferrer" className="project-link">
                  Visit Site <ExternalLink size={14} strokeWidth={2.25} />
                </a>
                </div>
              </div>
              <h3 className="project-name">{project.name}</h3>
              <p className="project-period">{project.period}</p>
              <p className="project-desc">{project.description}</p>
              <ul className="project-features">
                {project.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
              <div className="project-tags">
                {project.tags.map((tag, i) => (
                  <span key={i} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          ))}

          {/* More projects placeholder */}
          <div className="project-card coming-soon animate-on-scroll glass">
            <div className="coming-soon-content">
              <span className="coming-icon"><Sparkles size={30} strokeWidth={1.75} /></span>
              <h3>More Projects</h3>
              <p>New AI-powered projects coming soon...</p>
            </div>
          </div>
        </div>
      </section>

      {/* Writing / Articles Section */}
      {latestArticles.length > 0 && (
        <section id="writing" className="section writing-section">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Writing</span>
            <h2 className="section-title">Latest <span className="gradient-text">Articles</span></h2>
          </div>

          <div className="articles-grid animate-on-scroll">
            {latestArticles.map((a) => (
              <Link key={a.id} to={`/articles/${a.slug}`} className="article-card">
                {a.coverImage ? (
                  <img className="article-card-cover" src={a.coverImage} alt="" />
                ) : (
                  <div className="article-card-cover placeholder">
                    <PenLine size={28} />
                  </div>
                )}
                <div className="article-card-body">
                  {a.tags?.length > 0 && (
                    <div className="article-card-tags">
                      {a.tags.slice(0, 3).map((t) => (
                        <span key={t} className="article-tag">{t}</span>
                      ))}
                    </div>
                  )}
                  <h3 className="article-card-title">{a.title}</h3>
                  <p className="article-card-excerpt">{a.excerpt}</p>
                  <div className="article-card-meta">
                    <span>{formatDate(a.publishedAt || a.createdAt)}</span>
                    <span className="dot" />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={13} /> {a.readMinutes || 1} min
                    </span>
                    <span className="article-card-read">
                      Read <ArrowRight size={15} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <button className="writing-cta" onClick={() => navigate('/articles')}>
              View all articles <ArrowRight size={18} />
            </button>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="contact-content animate-on-scroll">
          <span className="section-tag">Get In Touch</span>
          <h2 className="contact-title">Let's Build Something <span className="gradient-text">Amazing</span></h2>
          <p className="contact-desc">
            Interested in AI solutions, enterprise integration, or just want to chat about technology? 
            I'm always open to discussing new projects and opportunities.
          </p>
          
          <div className="contact-methods">
            <a href={`mailto:${profile.email}`} className="contact-card glass">
              <span className="contact-icon"><Mail size={26} strokeWidth={1.9} /></span>
              <span className="contact-label">Email</span>
              <span className="contact-value">{profile.email}</span>
            </a>
            <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="contact-card glass">
              <span className="contact-icon"><LinkedinIcon size={26} /></span>
              <span className="contact-label">LinkedIn</span>
              <span className="contact-value">Connect with me</span>
            </a>
            <a href={profile.github} target="_blank" rel="noopener noreferrer" className="contact-card glass">
              <span className="contact-icon"><GithubIcon size={26} /></span>
              <span className="contact-label">GitHub</span>
              <span className="contact-value">View my code</span>
            </a>
          </div>

          <div className="contact-cta">
            <p className="cta-text">Ready to start a project?</p>
            <a href={`mailto:${profile.email}`} className="cta-button">
              <Send size={18} strokeWidth={2.25} className="cta-icon" />
              <span className="cta-label">Send me a message</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>© 2026 Abhiraj Kane. Built with React & passion.</p>
          <p className="footer-tagline">MuleSoft Solution Engineer • Enterprise Integration • Applied AI</p>
          <button
            className="footer-portal-link"
            onClick={() => navigate('/portal')}
            aria-label="Private portal"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>Portal</span>
          </button>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<div className="route-suspense-fallback" />}>
      <Routes>
        <Route path="/valentines" element={<Valentine />} />
        <Route 
          path="/portal/*" 
          element={
            <PrivatePortal>
              <MealPrepDashboard />
            </PrivatePortal>
          } 
        />
        {/* Writing / Articles */}
        <Route path="/articles" element={<ArticlesList />} />
        <Route
          path="/articles/admin/*"
          element={
            <PrivatePortal>
              <ArticleAdmin />
            </PrivatePortal>
          }
        />
        <Route path="/articles/:slug" element={<ArticleReader />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
